import type { TowerKind } from '../data/towers'
import type { MarketSymbol } from '../data/market'
import type { GameSnapshot, GameSpeed } from '../state/GameStore'
import type { ProtocolId } from '../data/protocols'
import type { PlayerProfile, RunResult } from '../progression/MetaProgress'
import type { TargetPriority } from '../entities/Tower'
import type { ContractId } from '../data/contracts'
import type { LoadoutId, TowerSynergyId } from '../data/loadouts'
import type { AscensionId } from '../data/ascensions'
import type { DisruptionId } from '../data/disruptions'
import type { EncounterDirectiveId } from '../data/encounters'
import type { RunMode } from '../data/endgame'

export type AudioCue = 'SHOT_SOL' | 'SHOT_ETH' | 'SHOT_BTC' | 'IMPACT' | 'DESTROY' | 'BUILD' | 'UPGRADE' | 'WAVE' | 'BREACH' | 'PROTOCOL' | 'VICTORY' | 'DEFEAT'

export interface BridgeEvents {
  engineReady: undefined
  engineError: { message: string }
  stateChanged: GameSnapshot
  buildNodeSelected: { nodeId: string; occupied: boolean }
  buildNodeCleared: undefined
  selectionCancelRequested: undefined
  towerSelected: { towerId: string; towerKind: TowerKind; level: number; targetPriority: TargetPriority; shotsFired: number; damageDealt: number; kills: number; refundValue: number; synergies: readonly TowerSynergyId[]; ascension: AscensionId | null; ascensionCost: number; abilityCooldownRemaining: number; disruptions: readonly DisruptionId[]; disruptionAttackSpeed: number; disruptionRange: number; protectedByPurge: boolean; countermeasure: string | null }
  towerSelectionCleared: undefined
  buildRequested: { nodeId: string; towerKind: TowerKind }
  buildRejected: { reason: string }
  notice: { message: string; tone: 'info' | 'success' | 'danger' }
  formationTriggered: { name: string; glyph: string; color: number; group: number; totalGroups: number; pathIndices: readonly number[] }
  restartRequested: undefined
  marketTradeRequested: { symbol: MarketSymbol; side: 'BUY' | 'SELL'; fraction: number }
  towerUpgradeRequested: { towerId: string }
  towerAscendRequested: { towerId: string; ascension: AscensionId }
  towerAbilityRequested: { towerId: string }
  towerTargetingRequested: { towerId: string; priority: TargetPriority }
  towerSellRequested: { towerId: string }
  gameSpeedRequested: { speed: GameSpeed }
  earlyWaveRequested: undefined
  autoDeployRequested: { enabled: boolean }
  contractSelected: { contract: ContractId }
  loadoutSelected: { loadout: LoadoutId }
  encounterSelected: { directive: EncounterDirectiveId }
  runModeSelected: { mode: RunMode }
  protocolChoiceRequested: { protocol: ProtocolId }
  tutorialChanged: { active: boolean }
  profileChanged: PlayerProfile
  runCompleted: RunResult
  audioCue: { cue: AudioCue }
}

type Handler<T> = (payload: T) => void

class GameBridge {
  private listeners = new Map<keyof BridgeEvents, Set<Handler<never>>>()

  on<K extends keyof BridgeEvents>(event: K, handler: Handler<BridgeEvents[K]>): () => void {
    const bucket = this.listeners.get(event) ?? new Set<Handler<never>>()
    bucket.add(handler as Handler<never>)
    this.listeners.set(event, bucket)
    return () => bucket.delete(handler as Handler<never>)
  }

  emit<K extends keyof BridgeEvents>(event: K, payload: BridgeEvents[K]): void {
    this.listeners.get(event)?.forEach((handler) => handler(payload as never))
  }
}

export const gameBridge = new GameBridge()
